/**
 * @module app.cd
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import * as CD from 'app/cd/models/essette-api';


export interface IDrugSearchFilters {
    general?: string;
    name?: string;
    code?: string;
    type?: CD.DrugType;
    category?: CD.DrugCategory;
    therapeuticClass?: string;
    ingredient?: string;
}

/**  Provides access to API models related to CD Drugs */
@transient()
@autoinject()
export class DrugService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(private readonly api: Api, private readonly logger: AppLogger) { }

    /** Returns the number of defined filters. Useful for checking that X filters exist before running a search */
    public countFilters(searchFilters: IDrugSearchFilters): number {
        if(!searchFilters) { return 0; }

        let count = 0;
        if(searchFilters.general) { count += 1; }
        if(searchFilters.name) { count += 1; }
        if(searchFilters.code) { count += 1; }
        if(searchFilters.type && searchFilters.type.id) { count += 1; }
        if(searchFilters.category && searchFilters.category.id) { count += 1; }
        if(searchFilters.therapeuticClass) { count += 1; }
        if(searchFilters.ingredient) { count += 1; }

        return count;
    }


    ////****************** Queries **************************************************////

    /**
     * Load all `CD.Drug` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadDrugs(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Drug>> {
        const query = this.api
            .query('cd/drugs')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async searchDrugs(searchFilters: IDrugSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Drug>> {
        const count = this.countFilters(searchFilters);
        if(count === 0) {
            this.logger.warn('Invalid or missing search criteria');
            return createQueryResults('CD.Drug', [], true);
        }

        if(count > 1 && searchFilters.general) { delete searchFilters.general; }

        const query = this.api
            .query('cd/drugs')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(searchFilters.general) {
            query.search(searchFilters.general);
        }
        else {
            if(searchFilters.name) {
                query.filterOr((or) => or
                    .filterString('brandName', searchFilters.name!, 'contains')
                    .filterString('genericName', searchFilters.name!, 'contains')
                );
            }

            if(searchFilters.code) {
                query.filterString('code', searchFilters.code, 'startsWith');
            }

            if(searchFilters.type && searchFilters.type.id) {
                query.filterIdentifier('type.id', searchFilters.type.id);
            }

            if(searchFilters.category && searchFilters.category.id) {
                query.filterIdentifier('category.id', searchFilters.category.id);
            }

            if(searchFilters.therapeuticClass) {
                query.filterExists('therapeuticClasses', (f =>
                    f.filterString('description', searchFilters.therapeuticClass!, 'contains')
                ));
            }

            if(searchFilters.ingredient) {
                query.filterExists('activeIngredients', (f =>
                    f.filterString('description', searchFilters.ingredient!, 'contains')
                ));
            }
        }

        return await this.api.get(query);
    }

    public async generalSearch(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Drug>> {
        const query = this.api
            .query('cd/drugs')
            .strategy('quick')
            .search(searchValue)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadDrugTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.DrugType>> {
        const query = this.api
            .query('cd/drugs/types')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    /**
     * Gets a specific `CD.Drug` record
     * @param drugId The id of the CD.Drug record to get
     * @param includes related models to include
     */
    public async loadDrug(drugId: string, includes: string[] = []): Promise<IApiQueryResults<CD.Drug>> {
        const query = this.api
            .query(`cd/drugs/${drugId}`)
            .include(...includes);
        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////

}
